import React, { useState } from 'react';
import authService from '../services/authService';
import { can } from '../utils/permissions';

const Sidebar = ({ activeItem, onNavigate, onLogout }) => {
  const user = authService.getCurrentUser();
  const role = user?.role?.toUpperCase() || 'STUDENT';
  const isStudent = role === 'STUDENT';

  // Finance submenu — auto-open if current screen is any finance screen
  const financeScreens = ['finance', 'studentFeeDetails', 'payCardDetails', 'payCardOperations'];
  const [financeOpen, setFinanceOpen] = useState(financeScreens.includes(activeItem));

  // ✅ Reports submenu — auto-open if current screen is reports
  const [reportsOpen, setReportsOpen] = useState(activeItem === 'reports');

  const allNavItems = [
    { id: 'dashboard',     label: 'Home',               icon: '⌂',  screen: null,         show: can('REGISTRATION') || can('ADMISSION') || can('FINANCE_FULL') || can('REPORTS') || can('ATTENDANCE_VIEW') },
    { id: 'registration',  label: 'Registration',        icon: '✎',  screen: 'dashboard',  show: can('REGISTRATION'),   activeOn: ['dashboard', 'registration', 'registrationView', 'registration1', 'registration2', 'registration3', 'registration4'] },
    { id: 'admission',     label: 'Admission',           icon: '☷',  screen: 'admission',  show: can('ADMISSION') },
    { id: 'rooms',         label: 'Rooms',               icon: '▣',  screen: 'rooms',      show: can('ROOM_ALLOCATION') },
    // Finance handled separately below as expandable submenu
    { id: 'canteen',       label: 'Canteen',             icon: '☕', screen: 'canteen',    show: can('CANTEEN_MENU_VIEW') },
    { id: 'inventory',     label: 'Inventory',           icon: '📦', screen: 'inventory',  show: can('CREATE_PR') || can('APPROVE_PR') || can('CREATE_CR') || can('APPROVE_CR') || can('RECEIVE_STOCK') || can('ISSUE_STOCK') },
    { id: 'notifications', label: 'Notifications',       icon: '🔔', screen: 'notifications', show: can('NOTIFICATION_TEMPLATES') },
    { id: 'attendance',    label: 'Attendance & Leave',  icon: '📅', screen: 'attendance', show: can('ATTENDANCE_VIEW') || can('APPLY_LEAVES') || can('APPROVE_LEAVES') },
    // Reports handled separately below as expandable submenu
    { id: 'users',         label: 'Users',               icon: '👥', screen: 'users',      show: can('ADD_DELETE_USER') || can('ADD_ADMIN') },
  ].filter(item => item.show !== false);

  const financeSubItems = [
    { id: 'finance',           label: 'Admission Fees',      icon: '💰' },
    { id: 'payCardOperations', label: 'Pay Card Operations', icon: '💳' },
  ];

  // ✅ Reports sub items
  const reportsSubItems = [
    { id: 'rep-registration', label: 'Student Registration', icon: '📋', reportType: 'registration', coming: false },
    { id: 'rep-admission',    label: 'Admissions',           icon: '🎓', reportType: 'admission',    coming: false },
    { id: 'rep-finance',     label: 'Finance',              icon: '💰', reportType: 'finance',      coming: false },
    { id: 'rep-attendance',   label: 'Attendance',           icon: '📅', reportType: 'attendance',   coming: true  },
    { id: 'rep-inventory',    label: 'Inventory',            icon: '📦', reportType: 'inventory',    coming: true  },
    { id: 'rep-bed-alloc',    label: 'Bed Allocation',       icon: '🛏', reportType: 'bed-allocation', coming: false },
    { id: 'rep-leaves',       label: 'Leaves',               icon: '📝', reportType: 'leaves',       coming: true  },
  ];

  // Split around Finance (after rooms, before canteen)
  const itemsBefore = allNavItems.filter(i => ['dashboard','registration','admission','rooms'].includes(i.id));
  const itemsMiddle = allNavItems.filter(i => ['canteen','inventory','notifications','attendance'].includes(i.id));
  const itemsAfter  = allNavItems.filter(i => ['users'].includes(i.id));

  const isFinanceActive = financeScreens.includes(activeItem);
  const isReportsActive = activeItem === 'reports';

  // Guest/Student sees only Registration + Rooms
  const studentItems = [
    { id: 'registration', label: 'Registration', icon: '✎', screen: 'registration1' },
    { id: 'rooms',        label: 'Rooms',         icon: '▣', screen: 'rooms' },
  ];

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      authService.logout();
      if (onLogout) onLogout();
    }
  };

  const renderItem = (item) => {
    const isActive = item.activeOn
      ? item.activeOn.includes(activeItem)
      : activeItem === item.id;
    return (
      <a key={item.id}
        className={`nav-item ${isActive ? 'active' : ''}`}
        onClick={e => { e.preventDefault(); item.screen && onNavigate(item.screen); }}
        style={{ cursor: item.screen ? 'pointer' : 'default', opacity: item.screen ? 1 : 0.5 }}
      >
        <span className="nav-icon">{item.icon}</span>{item.label}
      </a>
    );
  };

  const showFinance = can('FINANCE_FULL') || can('FINANCE_READ') || can('ACCOUNTING');
  const showReports = can('REPORTS');

  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <div className="sidebar-logo">OMKARA</div>
        <div className="sidebar-subtitle">Hostel Portal</div>
      </div>
      <nav className="sidebar-nav">
        {isStudent ? (
          studentItems.map(renderItem)
        ) : (
          <>
            {itemsBefore.map(renderItem)}

            {/* ── Finance expandable submenu ── */}
            {showFinance && (
              <a
                className={`nav-item ${isFinanceActive ? 'active' : ''}`}
                onClick={e => { e.preventDefault(); setFinanceOpen(o => !o); }}
                style={{ cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between' }}
              >
                <div style={{ display:'flex', alignItems:'center', gap:15 }}>
                  <span className="nav-icon">$</span>Finance
                </div>
                <span style={{ fontSize:9, opacity:0.7, display:'inline-block', transition:'transform 0.2s', transform: financeOpen ? 'rotate(180deg)' : 'rotate(0deg)', marginRight:4 }}>▼</span>
              </a>
            )}
            {showFinance && financeOpen && (
              <div style={{ background:'rgba(0,0,0,0.15)', borderLeft:'3px solid rgba(212,175,55,0.35)' }}>
                {financeSubItems.map(sub => (
                  <a key={sub.id}
                    className={`nav-item ${
                      activeItem === sub.id ||
                      (sub.id === 'finance' && ['studentFeeDetails','payCardDetails'].includes(activeItem))
                        ? 'active' : ''
                    }`}
                    onClick={e => { e.preventDefault(); onNavigate(sub.id); }}
                    style={{ cursor:'pointer', paddingLeft:44, fontSize:13 }}
                  >
                    <span style={{ marginRight:10 }}>{sub.icon}</span>{sub.label}
                  </a>
                ))}
              </div>
            )}

            {itemsMiddle.map(renderItem)}

            {/* ── Reports expandable submenu ── */}
            {showReports && (
              <a
                className={`nav-item ${isReportsActive ? 'active' : ''}`}
                onClick={e => { e.preventDefault(); setReportsOpen(o => !o); }}
                style={{ cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between' }}
              >
                <div style={{ display:'flex', alignItems:'center', gap:15 }}>
                  <span className="nav-icon">📊</span>Reports
                </div>
                <span style={{ fontSize:9, opacity:0.7, display:'inline-block', transition:'transform 0.2s', transform: reportsOpen ? 'rotate(180deg)' : 'rotate(0deg)', marginRight:4 }}>▼</span>
              </a>
            )}
            {showReports && reportsOpen && (
              <div style={{ background:'rgba(0,0,0,0.15)', borderLeft:'3px solid rgba(212,175,55,0.35)' }}>
                {reportsSubItems.map(sub => (
                  <a key={sub.id}
                    className={`nav-item ${isReportsActive && !sub.coming ? 'active' : ''}`}
                    onClick={e => {
                      e.preventDefault();
                      if (!sub.coming) onNavigate('reports', { reportType: sub.reportType });
                    }}
                    style={{ cursor: sub.coming ? 'not-allowed' : 'pointer', paddingLeft:44, fontSize:13, opacity: sub.coming ? 0.5 : 1 }}
                  >
                    <span style={{ marginRight:10 }}>{sub.icon}</span>
                    {sub.label}
                    {sub.coming && (
                      <span style={{ marginLeft:6, fontSize:9, fontWeight:700, letterSpacing:0.5, opacity:0.7 }}>SOON</span>
                    )}
                  </a>
                ))}
              </div>
            )}

            {itemsAfter.map(renderItem)}
          </>
        )}
      </nav>
      <div className="sidebar-footer">
        <div className="user-profile">
          <div className="user-avatar">{
            (user?.firstName || user?.lastName)
              ? `${user.firstName||''} ${user.lastName||''}`.trim().charAt(0).toUpperCase()
              : (user?.username ? user.username.charAt(0).toUpperCase() : 'U')
          }</div>
          <div className="user-info">
            <div className="user-name">{
              (user?.firstName || user?.lastName)
                ? `${user.firstName||''} ${user.lastName||''}`.trim()
                : (user?.username || 'User')
            }</div>
            <div className="user-role">{role.charAt(0) + role.slice(1).toLowerCase()}</div>
          </div>
        </div>
        <button onClick={handleLogout}
          style={{ width:'100%', marginTop:15, padding:10, background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.2)', color:'white', fontSize:12, fontWeight:600, letterSpacing:1, textTransform:'uppercase', cursor:'pointer', transition:'all 0.3s', borderRadius:4 }}
          onMouseOver={e => e.target.style.background = 'rgba(255,255,255,0.2)'}
          onMouseOut={e  => e.target.style.background = 'rgba(255,255,255,0.1)'}
        >Logout</button>
      </div>
    </aside>
  );
};

export default Sidebar;